// ─────────────────────────────────────────────────────────────
//  Einmalzahlungen konsistent halten (Definition ↔ Rechnung ↔ Auszahlung)
//
//  Gegenstück zu reportingSync.js: eine reine Funktion, die aus dem aktuellen
//  Datenstand die nötigen Firebase-Updates baut. Kein React, keine Seiteneffekte
//  – dadurch testbar und in jedem Dashboard wiederverwendbar.
//
//  Der Abgleich denkt von den erzeugten RECHNUNGEN aus, nicht von den
//  Definitionen. Nur so fallen auch Positionen auf, die in der Sales Pipeline
//  gelöscht wurden und deren Rechnung sonst verwaist stehen bliebe.
//
//  Grundregel (wie überall in der Abrechnung): Geld, das raus ist, bleibt wie es
//  ist. Angepasst wird ausschließlich, was noch offen ist.
//    • Definition weg/storniert -> offene Rechnung + offene Auszahlungen stornieren
//    • Position korrigiert      -> offene Rechnung zieht mit
//    • Anteil korrigiert        -> offene Auszahlungen ziehen mit
//    • Anteil entfernt          -> offene Auszahlung stornieren (reaktivierbar)
//
//  Idempotent: schreibt nur bei echter Abweichung und konvergiert.
// ─────────────────────────────────────────────────────────────
import {
  oneTimePeriods,
  oneTimeIdFromPeriodKey,
  oneTimeCommissionFor,
  findOneTime,
} from './paymentEngine';

export const PAYMENTS_PATH = 'salesgod-research/payments';
export const PAYOUTS_PATH = 'salesgod-research/payouts';

// Von uns gesetzter Storno-Grund. Daran erkennen wir beim Wiederhinzufügen eines
// Anteils, dass wir die Auszahlung selbst deaktiviert haben und sie reaktivieren
// dürfen – ein von Hand stornierter Posten bleibt dagegen storniert.
export const AUTO_CANCEL_REASON = 'Provisionsanteil in der Pipeline entfernt';

// ctx: { customers, payments, payouts, now? }
export function buildOneTimeSyncUpdates(ctx) {
  const { customers = {}, payments = {}, payouts = {}, now = Date.now() } = ctx || {};
  const updates = {};

  // Steuer-/Betragsberechnung je Kunde nur einmal ausführen.
  const entryCache = {};
  const entriesFor = (slug, c) => {
    if (!entryCache[slug]) {
      const map = {};
      // Fälligkeitsfilter aushebeln: ALLE Positionen vergleichen, nicht nur die
      // bereits fälligen.
      oneTimePeriods(c, Number.MAX_SAFE_INTEGER).forEach((e) => { map[e.periodKey] = e; });
      entryCache[slug] = map;
    }
    return entryCache[slug];
  };

  // Auszahlungen nach Rechnung gruppieren (einmal statt je Rechnung neu filtern).
  const payoutsByInvoice = {};
  Object.keys(payouts).forEach((poid) => {
    const po = payouts[poid];
    if (!po || po.type !== 'onetime' || !po.customerSlug || !po.periodKey) return;
    const key = `${po.customerSlug}__${po.periodKey}`;
    (payoutsByInvoice[key] || (payoutsByInvoice[key] = [])).push(poid);
  });

  Object.keys(payments).forEach((pid) => {
    const pay = payments[pid];
    if (!pay || pay.type !== 'onetime' || !pay.customerSlug || !pay.periodKey) return;
    const slug = pay.customerSlug;
    const c = customers[slug];
    if (!c) return; // Kunde (noch) nicht geladen -> nichts stornieren
    const ot = findOneTime(c, pay.oneTimeId || oneTimeIdFromPeriodKey(pay.periodKey));
    const related = payoutsByInvoice[`${slug}__${pay.periodKey}`] || [];

    // ── Definition gelöscht oder storniert ──
    if (!ot || ot.cancelled) {
      if (pay.status === 'due') {
        updates[`${PAYMENTS_PATH}/${pid}/status`] = 'cancelled';
        updates[`${PAYMENTS_PATH}/${pid}/cancelReason`] = ot
          ? 'Einmalzahlung in der Pipeline storniert'
          : 'Einmalzahlung in der Pipeline gelöscht';
        updates[`${PAYMENTS_PATH}/${pid}/cancelledAt`] = now;
      }
      related.forEach((poid) => {
        if (payouts[poid].status !== 'open') return;
        updates[`${PAYOUTS_PATH}/${poid}/status`] = 'cancelled';
        updates[`${PAYOUTS_PATH}/${poid}/cancelReason`] = AUTO_CANCEL_REASON;
        updates[`${PAYOUTS_PATH}/${poid}/cancelledAt`] = now;
        updates[`${PAYOUTS_PATH}/${poid}/updatedAt`] = now;
      });
      return;
    }

    // ── Rechnung angleichen, solange sie offen ist ──
    if (pay.status === 'due') {
      const entry = entriesFor(slug, c)[pay.periodKey];
      if (entry) {
        const fields = {
          label: entry.label,
          dueDate: entry.dueDate,
          amount: entry.amount,
          net: entry.net,
          vat: entry.vat,
          vatRate: entry.vatRate,
          taxMode: entry.taxMode,
        };
        if (Object.keys(fields).some((k) => pay[k] !== fields[k])) {
          Object.keys(fields).forEach((k) => { updates[`${PAYMENTS_PATH}/${pid}/${k}`] = fields[k]; });
          updates[`${PAYMENTS_PATH}/${pid}/updatedAt`] = now;
        }
      }
    }

    // ── Offene Auszahlungen angleichen ──
    related.forEach((poid) => {
      const po = payouts[poid];
      const share = po.memberId ? oneTimeCommissionFor(ot, po.memberId) : null;

      // Anteil entfernt -> offene Auszahlung stornieren.
      if (!share) {
        if (po.status !== 'open') return;
        updates[`${PAYOUTS_PATH}/${poid}/status`] = 'cancelled';
        updates[`${PAYOUTS_PATH}/${poid}/cancelReason`] = AUTO_CANCEL_REASON;
        updates[`${PAYOUTS_PATH}/${poid}/cancelledAt`] = now;
        updates[`${PAYOUTS_PATH}/${poid}/updatedAt`] = now;
        return;
      }

      // Anteil wieder da, nachdem WIR storniert hatten -> reaktivieren.
      const reopen = po.status === 'cancelled' && po.cancelReason === AUTO_CANCEL_REASON;
      if (po.status !== 'open' && !reopen) return; // bezahlt / von Hand storniert: nie anfassen

      const fields = {
        amount: share.amount,
        baseAmount: share.amount,
        commissionMode: share.mode,
        commissionValue: share.value,
        commissionBase: share.base,
        commissionAmount: share.amount,
        label: ot.label,
      };
      if (!reopen && !Object.keys(fields).some((k) => po[k] !== fields[k])) return;
      Object.keys(fields).forEach((k) => { updates[`${PAYOUTS_PATH}/${poid}/${k}`] = fields[k]; });
      if (reopen) {
        updates[`${PAYOUTS_PATH}/${poid}/status`] = 'open';
        updates[`${PAYOUTS_PATH}/${poid}/cancelReason`] = null;
        updates[`${PAYOUTS_PATH}/${poid}/cancelledAt`] = null;
      }
      updates[`${PAYOUTS_PATH}/${poid}/updatedAt`] = now;
    });
  });

  return updates;
}
